import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { useUpcomingLives } from '../../hooks/useUpcomingLives';
import { useTheme } from '../../contexts/ThemeContext';
import { useDirection } from '../../contexts/DirectionContext';
import CountdownTimer from '../common/CountdownTimer';
import { ActionButton } from '../common/ActionButton';
import { VideoContent } from '../../types/content';
import { motion, AnimatePresence } from 'framer-motion';

interface UpcomingLivesContainerProps {
  className?: string;
}

const ITEMS_TO_SHOW = 5;

const StartingInBadge: React.FC<{ startTime: string; isRTL: boolean }> = ({ startTime, isRTL }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-lg shadow-lg">
      <span className="text-xs font-light tracking-wider uppercase text-white/80">
        {t('lives.starts')}
      </span>
      <span className="text-sm font-medium text-status-warning">
        <CountdownTimer startTime={startTime} />
      </span>
    </div>
  );
};

const LiveCard: React.FC<{
  video: VideoContent;
  isRTL: boolean;
  isDescriptionExpanded: boolean;
  onDescriptionExpandChange: (expanded: boolean) => void;
}> = ({ video, isRTL, isDescriptionExpanded, onDescriptionExpandChange }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  const handleAddToCalendar = () => {
    const event = {
      title: video.title,
      description: video.description,
      start: video.scheduledStartTime!,
      duration: video.duration,
      url: video.url
    };
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${
      encodeURIComponent(event.title)
    }&details=${
      encodeURIComponent(event.description)
    }&dates=${
      new Date(event.start).toISOString().replace(/[-:.]/g, '')
    }/${
      new Date(Date.parse(event.start) + event.duration * 1000).toISOString().replace(/[-:.]/g, '')
    }`);
  };

  const handleSetReminder = () => {
    // TODO: Implement reminder functionality
  };

  const toggleDescription = () => {
    onDescriptionExpandChange(!isDescriptionExpanded);
  };

  return (
    <motion.div
      initial={{ 
        opacity: 0,
        rotateY: isRTL ? -45 : 45,
        scale: 0.9
      }}
      animate={{ 
        opacity: 1,
        rotateY: 0,
        scale: 1,
        transition: {
          type: "spring",
          stiffness: 100,
          damping: 20
        }
      }}
      exit={{ 
        opacity: 0,
        rotateY: isRTL ? 45 : -45,
        scale: 0.9,
        transition: {
          duration: 0.3
        }
      }}
      className="absolute inset-0 flex flex-col [transform-style:preserve-3d] [perspective:1000px]"
    >
      {/* Fixed height background container */}
      <div className="absolute inset-0">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${video.thumbnail})` }}
        />
        
        {/* Dark Overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
      </div>
      
      {/* Content */}
      <div className={`relative flex-1 flex flex-col p-4 sm:p-6 md:p-8 transition-all duration-200 ${
        isDescriptionExpanded 
          ? 'bg-gradient-to-t from-black via-black/90 to-transparent'
          : 'bg-gradient-to-t from-black/90 to-transparent'
      }`}>
        {/* Top Section */}
        <div className="flex items-center justify-between w-full gap-3">
          <div className="shrink-0">
            <StartingInBadge startTime={video.scheduledStartTime!} isRTL={isRTL} />
          </div>
          <div className="flex items-center gap-2">
            <ActionButton
              onClick={handleAddToCalendar}
              icon="ph:calendar-plus-thin"
              label={t('lives.addToCalendar')}
              size="icon"
              className="hover:scale-105 transition-transform shadow-lg bg-black/40 backdrop-blur-sm"
            />
            <ActionButton
              onClick={handleSetReminder}
              icon="ph:bell-thin"
              label={t('lives.setReminder')}
              size="icon"
              className="hover:scale-105 transition-transform shadow-lg bg-black/40 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="mt-auto">
          <a 
            href={`/video/${video.id}`}
            className="block group mb-2"
          >
            <h3 className={`text-base sm:text-xl md:text-2xl lg:text-xl font-semibold text-white line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors ${isRTL ? 'text-right' : 'text-left'}`}>
              {video.title}
            </h3>
          </a>
          {video.description && (
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <p 
                className={`text-sm sm:text-base text-white/70 ${isDescriptionExpanded ? '' : 'line-clamp-2'} cursor-pointer hover:text-white transition-colors`}
                onClick={toggleDescription}
              >
                {video.description}
              </p>
              {isDescriptionExpanded && (
                <button
                  onClick={toggleDescription}
                  className="mt-2 text-xs text-primary-500 hover:text-primary-400 transition-colors"
                >
                  {t('common.seeLess')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const UpcomingLivesContainer: React.FC<UpcomingLivesContainerProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  
  const { videos, loading } = useUpcomingLives({
    limit: ITEMS_TO_SHOW,
    enabled: true,
  });

  useEffect(() => {
    if (videos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % videos.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [videos.length]);

  if (loading || !videos.length) {
    return null;
  }

  const showNavigation = videos.length > 1;
  const currentVideo = videos[currentIndex];

  return (
    <div 
      className={`overflow-hidden sm:rounded-xl shadow-lg mx-auto w-full max-w-4xl border border-border-primary ${className}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header Section */}
      <div className={`${theme.mode === "dark" ? "bg-surface-secondary" : "bg-background-secondary"} border-b border-border-primary`}>
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              {t('lives.upcomingStreams')}
            </h2>
            {showNavigation && (
              <span className="text-sm text-text-secondary">
                {currentIndex + 1} / {videos.length}
              </span>
            )}
          </div>

          {/* Navigation Controls */}
          {showNavigation && (
            <div className="inline-flex">
              <motion.button
                whileHover={{ scale: currentIndex === 0 ? 1 : 1.05 }}
                whileTap={{ scale: currentIndex === 0 ? 1 : 0.95 }}
                onClick={() => setCurrentIndex((prev) => isRTL 
                  ? (prev - 1 + videos.length) % videos.length
                  : (prev - 1 + videos.length) % videos.length
                )}
                disabled={currentIndex === 0}
                className={`p-1.5 rounded-l-lg transition-colors ${
                  currentIndex === 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-white/10'
                }`}
                aria-label={isRTL ? t('common.next') : t('common.previous')}
              >
                <Icon 
                  icon={isRTL ? 'ph:caret-right-thin' : 'ph:caret-left-thin'}
                  className="w-3.5 h-3.5 text-text-primary"
                />
              </motion.button>
              <motion.button
                whileHover={{ scale: currentIndex === videos.length - 1 ? 1 : 1.05 }}
                whileTap={{ scale: currentIndex === videos.length - 1 ? 1 : 0.95 }}
                onClick={() => setCurrentIndex((prev) => isRTL 
                  ? (prev + 1) % videos.length
                  : (prev + 1) % videos.length
                )}
                disabled={currentIndex === videos.length - 1}
                className={`p-1.5 rounded-r-lg transition-colors ${
                  currentIndex === videos.length - 1 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-white/10'
                }`}
                aria-label={isRTL ? t('common.previous') : t('common.next')}
              >
                <Icon 
                  icon={isRTL ? 'ph:caret-left-thin' : 'ph:caret-right-thin'}
                  className="w-3.5 h-3.5 text-text-primary"
                />
              </motion.button>
            </div>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className={`relative bg-black transition-[min-height] duration-200 ${isDescriptionExpanded ? 'min-h-[360px]' : 'min-h-[240px]'}`}>
        <AnimatePresence mode="wait" initial={false}>
          <LiveCard 
            key={currentIndex}
            video={currentVideo}
            isRTL={isRTL}
            isDescriptionExpanded={isDescriptionExpanded}
            onDescriptionExpandChange={setIsDescriptionExpanded}
          />
        </AnimatePresence>
      </div>
    </div>
  );
}; 